import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import PublicInfoLayout from '../components/public/PublicInfoLayout';

export default function PrivacyPage() {
  useEffect(() => {
    document.title = 'Privacy Policy | Keller Williams Southern Africa';
  }, []);

  return (
    <PublicInfoLayout>
      <div className="rounded-2xl border border-slate-200/80 bg-white p-6 sm:p-10 shadow-sm space-y-8">
        {/* Header */}
        <div className="border-b border-slate-100 pb-6">
          <div className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
            Legal &amp; Privacy
          </div>
          <h1 className="mt-3 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            Privacy Policy
          </h1>
          <p className="mt-2 text-sm text-slate-500">
            Mega Agent Productivity Platform (MAPP) &bull; Keller Williams Southern Africa
          </p>
          <p className="text-xs text-slate-400 mt-1">
            Effective Date: October 2026
          </p>
        </div>

        {/* Section 1: About MAPP */}
        <section className="space-y-3">
          <h2 className="text-xl font-bold text-slate-900">
            1. About MAPP
          </h2>
          <p className="text-sm sm:text-base text-slate-700 leading-relaxed">
            The Mega Agent Productivity Platform (&ldquo;MAPP&rdquo;) is the business operations software platform operated for
            Keller Williams Southern Africa (&ldquo;KWSA&rdquo;), its affiliated Market Centres, and authorized real estate
            associates across South Africa.
          </p>
          <p className="text-sm sm:text-base text-slate-700 leading-relaxed">
            MAPP provides centralized tools and infrastructure to manage property listings, sales and rental transactions,
            agent productivity tracking, Comparative Market Analysis (CMA) reports, training resources, and client
            correspondence. MAPP is an enterprise platform intended strictly for legitimate real estate business workflows
            by authorized personnel.
          </p>
        </section>

        {/* Section 2: Information We Process */}
        <section className="space-y-3">
          <h2 className="text-xl font-bold text-slate-900">
            2. Information We Process
          </h2>
          <p className="text-sm sm:text-base text-slate-700 leading-relaxed">
            MAPP processes information necessary to facilitate day-to-day real estate operations, mandate execution, and brokerage compliance:
          </p>
          <ul className="list-disc pl-5 space-y-1.5 text-sm sm:text-base text-slate-700 leading-relaxed">
            <li>
              <strong>Associate &amp; Staff Profile Data:</strong> Full name, professional KWSA email address
              (ending in @kwsa.co.za or @kwsouthafrica.co.za), profile photo, operational role, Market Centre
              affiliation, and system identifiers.
            </li>
            <li>
              <strong>Listing and Mandate Information:</strong> Property addresses, listing specifications, pricing,
              marketing photography, portal syndication statuses, and mandate agreements.
            </li>
            <li>
              <strong>Client and Contact Information:</strong> Names, email addresses, and phone numbers of property sellers,
              buyers, landlords, and tenants provided by authorized associates for contract execution and service delivery.
            </li>
            <li>
              <strong>Portal Inquiries and Leads:</strong> Prospective buyer and tenant inquiries submitted through authorized
              real estate portals (e.g. Property24, Private Property, KWHomes) and assigned to associates.
            </li>
            <li>
              <strong>Technical and Audit Records:</strong> IP addresses, browser types, session timestamps, and operational action
              logs collected automatically to safeguard platform security and maintain audit trails.
            </li>
          </ul>
        </section>

        {/* Section 3: How We Use Information */}
        <section className="space-y-3">
          <h2 className="text-xl font-bold text-slate-900">
            3. How We Use Information
          </h2>
          <p className="text-sm sm:text-base text-slate-700 leading-relaxed">
            Information collected and processed within MAPP is used solely to deliver real estate brokerage services, operational management, and client communication:
          </p>
          <ul className="list-disc pl-5 space-y-1.5 text-sm sm:text-base text-slate-700 leading-relaxed">
            <li>Facilitating the management, review, approval, and multi-portal distribution of property listings.</li>
            <li>Processing real estate transaction files, commission tracking, and Market Centre production reporting.</li>
            <li>Generating and sending structured Seller Updates, Landlord Updates, and listing activity reports to clients.</li>
            <li>Enabling authorized associates to dispatch client communications directly from their verified corporate email addresses.</li>
            <li>Providing operational metrics, production tracking, and leadership oversight for Market Centres.</li>
            <li>Ensuring organizational compliance with real estate regulatory requirements, system integrity, and audit standards.</li>
          </ul>
        </section>

        {/* Section 4: Listing, Lead and Client Information */}
        <section className="space-y-3">
          <h2 className="text-xl font-bold text-slate-900">
            4. Listing, Lead and Client Information
          </h2>
          <p className="text-sm sm:text-base text-slate-700 leading-relaxed">
            All property listings, client details, and prospective leads stored in MAPP represent proprietary and confidential business records.
          </p>
          <p className="text-sm sm:text-base text-slate-700 leading-relaxed">
            Data is partitioned strictly according to Market Centre boundaries and associate assignments. Associates and leadership may access only information pertinent to their authorized operational scope. KWSA does not sell, rent, monetize, or trade client contact details, leads, or consumer data to third parties, external advertisers, or commercial data brokers.
          </p>
        </section>

        {/* Section 5: Mandatory Google Section */}
        <section className="space-y-4 rounded-xl border-2 border-blue-200 bg-blue-50/60 p-6 sm:p-7">
          <div className="flex items-center gap-2">
            <svg className="h-6 w-6 text-blue-700 shrink-0" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 15h2v-6h-2v6zm0-8h2V7h-2v2z" />
            </svg>
            <h2 className="text-xl font-bold text-blue-950">
              5. Google Account and Gmail Data
            </h2>
          </div>

          <div className="space-y-3 text-sm sm:text-base text-slate-800 leading-relaxed font-normal">
            <p>
              MAPP allows authorised Keller Williams Southern Africa associates to connect
              an approved Google Workspace account ending in @kwsa.co.za or
              @kwsouthafrica.co.za.
            </p>
            <p>
              MAPP requests Google permissions only to identify the connected Google account
              and to send email on behalf of the authenticated associate.
            </p>
            <p className="font-semibold text-slate-900">
              MAPP does not request permission to read, search, download or access the
              user&apos;s Gmail inbox, received messages or message history.
            </p>
            <p>
              The Gmail connection is used to allow the associate to send Seller and
              Landlord Update communications from their own approved mailbox.
            </p>
            <p>
              OAuth credentials are associated with the authenticated MAPP associate and
              are used only to provide this email-sending functionality.
            </p>
            <p>
              Google user data is not sold, used for advertising, or transferred for
              advertising purposes.
            </p>
            <p>
              MAPP&apos;s use and transfer of information received from Google APIs complies
              with the Google API Services User Data Policy, including the Limited Use
              requirements.
            </p>
          </div>
        </section>

        {/* Section 6: Google API Services User Data Policy / Limited Use */}
        <section className="space-y-3">
          <h2 className="text-xl font-bold text-slate-900">
            6. Google API Services User Data Policy / Limited Use
          </h2>
          <p className="text-sm sm:text-base text-slate-700 leading-relaxed">
            MAPP adheres strictly to the <strong>Google API Services User Data Policy</strong>, specifically including the <strong>Limited Use</strong> requirements:
          </p>
          <ul className="list-disc pl-5 space-y-1.5 text-sm sm:text-base text-slate-700 leading-relaxed">
            <li><strong>Specific Functionality:</strong> MAPP uses Google Workspace permissions exclusively to provide user-facing features prominently visible within the MAPP interface—specifically dispatching property performance reports and client updates initiated directly by the associate.</li>
            <li><strong>No Transfer to Third Parties:</strong> Google user data is not transferred or shared with any third parties, unless necessary to provide or improve the core email-sending feature, to comply with applicable law, or as part of a corporate reorganization.</li>
            <li><strong>No Advertising:</strong> Google user data is never used or transferred for serving advertisements, retargeting, personalized advertising, or market research.</li>
            <li><strong>No Machine Learning / AI Training:</strong> Google user data is not used to train generalized artificial intelligence or machine learning models.</li>
            <li><strong>Prohibition on Human Inspection:</strong> Humans are not permitted to read or inspect Google user data, except where the user gives explicit consent for troubleshooting a specific support issue, where required for security purposes, or where mandated by legal process.</li>
          </ul>
        </section>

        {/* Section 7: How Data Is Protected */}
        <section className="space-y-3">
          <h2 className="text-xl font-bold text-slate-900">
            7. How Data Is Protected
          </h2>
          <p className="text-sm sm:text-base text-slate-700 leading-relaxed">
            KWSA employs technical, physical, and administrative safeguards designed to protect personal and business data against unauthorized access, destruction, loss, or disclosure:
          </p>
          <ul className="list-disc pl-5 space-y-1.5 text-sm sm:text-base text-slate-700 leading-relaxed">
            <li><strong>Transport Layer Security:</strong> All data transmitted between clients, the MAPP application, and external APIs is encrypted using modern Transport Layer Security (TLS 1.3 / HTTPS).</li>
            <li><strong>Restricted Token Storage:</strong> OAuth tokens and sensitive credentials are encrypted and stored in secure backend environments with restricted access.</li>
            <li><strong>Isolated Infrastructure:</strong> Applications are hosted on Google Cloud Platform with segregated virtual private networks, strict firewall rules, and hardened container configurations.</li>
            <li><strong>Least Privilege Architecture:</strong> Backend microservices and administrative personnel access only the minimum resources required for system operations.</li>
          </ul>
        </section>

        {/* Section 8: Data Sharing */}
        <section className="space-y-3">
          <h2 className="text-xl font-bold text-slate-900">
            8. Data Sharing
          </h2>
          <p className="text-sm sm:text-base text-slate-700 leading-relaxed">
            MAPP shares information only within authorized business channels required to execute real estate services:
          </p>
          <ul className="list-disc pl-5 space-y-1.5 text-sm sm:text-base text-slate-700 leading-relaxed">
            <li><strong>Within the KWSA Network:</strong> Information is shared between associates, their assigned Market Centre administrators, and regional leadership in accordance with operational governance.</li>
            <li><strong>Authorized Service Providers:</strong> Trusted infrastructure and technical providers (such as cloud hosting and database infrastructure) bound by strict confidentiality obligations.</li>
            <li><strong>Property Portals:</strong> Listing information is distributed to public portals (such as Property24, Private Property, and KWHomes) in accordance with agent mandate instructions.</li>
            <li><strong>Legal Requirements:</strong> Disclosures made when required by applicable legislation, valid court orders, or authorized regulatory bodies (e.g., the Property Practitioners Regulatory Authority - PPRA).</li>
          </ul>
          <p className="text-sm sm:text-base text-slate-700 leading-relaxed">
            KWSA does not sell, rent, or lease any personal data or contact records to commercial data brokers or advertisers.
          </p>
        </section>

        {/* Section 9: Retention */}
        <section className="space-y-3">
          <h2 className="text-xl font-bold text-slate-900">
            9. Retention
          </h2>
          <p className="text-sm sm:text-base text-slate-700 leading-relaxed">
            We retain operational data in accordance with statutory requirements and business necessity:
          </p>
          <ul className="list-disc pl-5 space-y-1.5 text-sm sm:text-base text-slate-700 leading-relaxed">
            <li><strong>Listing and Mandate Records:</strong> Retained for periods required by real estate governance, tax, and statutory property laws in South Africa.</li>
            <li><strong>Google OAuth Credentials:</strong> Maintained only as long as the associate retains an active integration. Associates may disconnect their Google account at any time through MAPP, which immediately deletes and revokes stored OAuth tokens.</li>
            <li><strong>System Audit Logs:</strong> Maintained for security auditing, anomaly detection, and operational debugging, and pruned on a regular retention schedule.</li>
          </ul>
        </section>

        {/* Section 10: User Access and Responsibilities */}
        <section className="space-y-3">
          <h2 className="text-xl font-bold text-slate-900">
            10. User Access and Responsibilities
          </h2>
          <p className="text-sm sm:text-base text-slate-700 leading-relaxed">
            Authorized associates and Market Centre personnel are responsible for:
          </p>
          <ul className="list-disc pl-5 space-y-1.5 text-sm sm:text-base text-slate-700 leading-relaxed">
            <li>Safeguarding login credentials and ensuring that connected Google Workspace accounts are secured with strong passwords and two-factor authentication.</li>
            <li>Ensuring that connected Google accounts are official KWSA Workspace accounts (@kwsa.co.za or @kwsouthafrica.co.za).</li>
            <li>Entering accurate and lawful client information, ensuring consent has been obtained for real estate communications.</li>
            <li>Promptly disconnecting integrations and reporting any suspicious activity or credential compromise to KWSA support.</li>
          </ul>
        </section>

        {/* Section 11: Security */}
        <section className="space-y-3">
          <h2 className="text-xl font-bold text-slate-900">
            11. Security
          </h2>
          <p className="text-sm sm:text-base text-slate-700 leading-relaxed">
            Security is an integral part of MAPP operations. We maintain proactive monitoring, access logging, and regular vulnerability assessments. In the event of a detected security incident affecting user credentials or personal data, KWSA maintains incident response procedures to rapidly investigate, mitigate, and notify affected parties in accordance with applicable data protection requirements.
          </p>
        </section>

        {/* Section 12: Changes to This Policy */}
        <section className="space-y-3">
          <h2 className="text-xl font-bold text-slate-900">
            12. Changes to This Policy
          </h2>
          <p className="text-sm sm:text-base text-slate-700 leading-relaxed">
            KWSA may update this Privacy Policy from time to time to reflect operational enhancements, new platform integrations, or legal requirements. The updated version will be indicated by the &ldquo;Effective Date&rdquo; at the top of this page. We encourage users and visitors to periodically review this page to stay informed about how we protect information.
          </p>
        </section>

        {/* Section 13: Contact Information */}
        <section className="space-y-3 border-t border-slate-100 pt-6">
          <h2 className="text-xl font-bold text-slate-900">
            13. Contact Information
          </h2>
          <p className="text-sm sm:text-base text-slate-700 leading-relaxed">
            If you have questions, concerns, or requests regarding this Privacy Policy, our data practices, or Google Workspace integration, please contact our monitored support channel:
          </p>
          <div className="rounded-lg bg-slate-50 p-4 border border-slate-200 text-sm text-slate-700">
            <p><strong>Platform:</strong> Mega Agent Productivity Platform (MAPP)</p>
            <p><strong>Operator:</strong> Keller Williams Southern Africa</p>
            <p><strong>Support Email:</strong> <a href="mailto:support@kwsa.co.za" className="text-red-700 hover:underline">support@kwsa.co.za</a></p>
            <p><strong>General Inquiries:</strong> <a href="mailto:info@kwsa.co.za" className="text-red-700 hover:underline">info@kwsa.co.za</a></p>
          </div>
        </section>

        {/* Footer Navigation within Page */}
        <div className="border-t border-slate-200 pt-6 flex flex-wrap items-center justify-between gap-4 text-sm">
          <div className="flex items-center gap-4">
            <Link to="/about" className="font-medium text-slate-600 hover:text-red-700 transition-colors">
              &larr; About MAPP
            </Link>
            <span className="text-slate-300">|</span>
            <Link to="/terms" className="font-medium text-slate-600 hover:text-red-700 transition-colors">
              Terms of Service &rarr;
            </Link>
          </div>
          <Link to="/login" className="font-medium text-red-700 hover:underline">
            Sign In to MAPP
          </Link>
        </div>
      </div>
    </PublicInfoLayout>
  );
}
